'use client';

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import { AI_CHAT_ENABLED, PAGES_WITHOUT_AI_CHAT } from '@/constants/ai-chat';
import { trackEvent } from '@/utils/analytics';

import { AiChatFab } from './AiChatFab.client';

// The runtime chunk (CopilotKit core + UI, react-markdown, the Panda chat UI, common.json) is
// requested on the first click only. Until then the page carries just this launcher and the FAB.
const AiChatRuntimeChunk = dynamic(() => import('./AiChatRuntimeChunk.client'), { ssr: false });

// Matches the panel's exit animation (AiChatPanel) — the panel stays mounted that long so
// the animation can play before React removes it.
const PANEL_EXIT_MS = 280;

const noopSubscribe = () => () => {};

type Props = {
  locale: string;
  fabLabel: string;
  fabAriaLabel: string;
};

/**
 * components/ai-chat/AiChatLauncher for the App Router. The Pages Router mounted it with
 * `next/dynamic({ ssr: false })`; here it renders nothing on the server / first client render
 * (same SSR HTML, no extra chunk), then the FAB mounts with its entrance animation.
 */
export const AiChatLauncher = ({ locale, fabLabel, fabAriaLabel }: Props) => {
  const pathname = usePathname();
  const isClient = useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );
  const isVisible = AI_CHAT_ENABLED && !PAGES_WITHOUT_AI_CHAT.includes(pathname ?? '');
  const [hasOpened, setHasOpened] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  // One `ai_chat_open` per page visit — reopening the panel isn't a new intent.
  const hasTrackedOpenRef = useRef(false);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  useEffect(() => cancelClose, [cancelClose]);

  const openPanel = () => {
    if (!hasTrackedOpenRef.current) {
      hasTrackedOpenRef.current = true;
      trackEvent({ event: 'ai_chat_open' });
    }
    cancelClose();
    setHasOpened(true);
    setIsClosing(false);
    setIsOpen(true);
  };

  const closePanel = useCallback(() => {
    cancelClose();
    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setIsOpen(false);
      setIsClosing(false);
    }, PANEL_EXIT_MS);
  }, [cancelClose]);

  if (!isClient || !isVisible) return null;

  return (
    <>
      <AiChatFab isOpen={isOpen && !isClosing} onClick={openPanel} label={fabLabel} ariaLabel={fabAriaLabel} />
      {hasOpened && <AiChatRuntimeChunk locale={locale} isOpen={isOpen} isClosing={isClosing} onClose={closePanel} />}
    </>
  );
};
